import { useEffect, useMemo, useState, type FormEvent } from "react";

import type { EventKindInfo, Me, Severity } from "../lib/api";
import { useEventKinds, useSetAlertKinds } from "../lib/queries";
import { Loading } from "./Loading";
import { useToast } from "./Toast";

const GROUPS: { severity: Severity; name: string; hint: string }[] = [
  { severity: "critical", name: "Alerte immédiate", hint: "L'entreprise risque de ne plus payer ou de disparaître." },
  { severity: "high", name: "Important", hint: "Difficultés déclarées ou fin d'existence juridique." },
  { severity: "medium", name: "À savoir", hint: "L'entreprise change : vérifiez que vos contrats suivent." },
  { severity: "low", name: "Pour information", hint: "Signes de vie administrative." },
];

const ESSENTIAL: Severity[] = ["critical", "high"];

export function AlertPreferences({ me }: { me: Me }) {
  const kinds = useEventKinds();
  const save = useSetAlertKinds();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(me.alert_kinds));

  // Follow the server once it answers (e.g. after saving).
  useEffect(() => setSelected(new Set(me.alert_kinds)), [me.alert_kinds]);

  const catalogue = useMemo(() => kinds.data ?? [], [kinds.data]);
  const dirty =
    selected.size !== me.alert_kinds.length || me.alert_kinds.some((k) => !selected.has(k));

  if (kinds.isPending) return <Loading label="Chargement des types d'événements" />;
  if (kinds.isError) return <div className="notice notice--error">{kinds.error.message}</div>;

  const setMany = (list: EventKindInfo[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const k of list) {
        if (on) next.add(k.kind);
        else next.delete(k.kind);
      }
      return next;
    });

  const preset = (severities: Severity[] | "all" | "none") =>
    setSelected(
      new Set(
        catalogue
          .filter((k) => severities === "all" || (severities !== "none" && severities.includes(k.severity)))
          .map((k) => k.kind),
      ),
    );

  function submit(e: FormEvent) {
    e.preventDefault();
    // Keep catalogue order so the saved list is stable.
    save.mutate(
      catalogue.filter((k) => selected.has(k.kind)).map((k) => k.kind),
      {
        onSuccess: () => toast("Préférences d'alerte enregistrées."),
        onError: (error) => toast(error.message, "error"),
      },
    );
  }

  return (
    <form className="alert-prefs" onSubmit={submit} aria-describedby="alert-prefs-summary">
      <div className="alert-prefs__presets" role="group" aria-label="Sélections rapides">
        <button type="button" className="button button--quiet button--small" onClick={() => preset("all")}>
          Tout recevoir
        </button>
        <button type="button" className="button button--quiet button--small" onClick={() => preset(ESSENTIAL)}>
          Procédures et cessations seulement
        </button>
        <button type="button" className="button button--quiet button--small" onClick={() => preset("none")}>
          Tout décocher
        </button>
      </div>

      {GROUPS.map((group) => {
        const members = catalogue.filter((k) => k.severity === group.severity);
        if (!members.length) return null;
        const count = members.filter((k) => selected.has(k.kind)).length;
        return (
          <fieldset key={group.severity} className={`alert-group alert-group--${group.severity}`}>
            <legend className="alert-group__legend">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={count === members.length}
                  ref={(el) => {
                    if (el) el.indeterminate = count > 0 && count < members.length;
                  }}
                  onChange={() => setMany(members, count < members.length)}
                />
                <span className="alert-group__name">{group.name}</span>
                <span className="field__hint">
                  {count} sur {members.length}
                </span>
              </label>
            </legend>
            <p className="field__hint alert-group__hint">{group.hint}</p>
            <div className="alert-group__kinds">
              {members.map((k) => (
                <label key={k.kind} className="alert-kind">
                  <input
                    type="checkbox"
                    checked={selected.has(k.kind)}
                    onChange={(e) => setMany([k], e.target.checked)}
                  />
                  <span>
                    <span className="alert-kind__label">{k.label}</span>
                    <span className="alert-kind__description">{k.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}

      {selected.size === 0 && (
        <div className="notice notice--warning" role="status">
          Vous ne recevrez plus aucun e-mail d'alerte. Les événements restent visibles sur les fiches des
          entreprises.
        </div>
      )}

      <div className="alert-prefs__save">
        <p id="alert-prefs-summary" className="field__hint">
          {selected.size === catalogue.length
            ? "Vous recevez tous les types d'événements, y compris ceux ajoutés à l'avenir."
            : `${selected.size} types d'événements sur ${catalogue.length} envoyés par e-mail.`}
          {dirty && " Modifications non enregistrées."}
        </p>
        <button type="submit" className="button" disabled={!dirty || save.isPending}>
          {save.isPending ? "Enregistrement" : "Enregistrer les préférences"}
        </button>
      </div>
      <p className="field__hint">
        Cocher un type plus tard n'envoie pas les événements déjà publiés : seules les nouveautés suivantes le
        seront.
      </p>
    </form>
  );
}
